import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiMapPin, FiSave, FiRefreshCw } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import { NEAR_LOCATION_DEFAULTS, positiveKm } from '../../utils/nearLocationConfig';
import './AdminPages.css';
import './NearLocationConfigManagement.css';

const authHeaders = () => ({ Authorization: `Bearer ${getAuthToken()}` });

const clampInt = (n, min, max, fb) => {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return fb;
  return Math.min(max, Math.max(min, v));
};

/** Distance field: free number input + optional slider (no hard max). */
const DistanceKmField = ({
  label,
  hint,
  value,
  onChange,
  sliderMax,
}) => {
  const num = Number(value);
  const safe = Number.isFinite(num) && num >= 1 ? num : 1;
  const trackMax = Math.max(sliderMax || 100, safe, 1);

  const handleNumber = (raw) => {
    const next = Number(raw);
    if (!Number.isFinite(next)) return;
    onChange(Math.max(1, next));
  };

  return (
    <div className="near-loc-field near-loc-distance-field">
      <label>{label}</label>
      <div className="near-loc-distance-card">
        <div className="near-loc-distance-input-row">
          <input
            type="number"
            className="near-loc-distance-number"
            min={1}
            step={1}
            value={safe}
            onChange={(e) => handleNumber(e.target.value)}
            aria-label={`${label} in kilometres`}
          />
          <span className="near-loc-distance-unit">km</span>
        </div>
        <input
          type="range"
          className="near-loc-distance-slider"
          min={1}
          max={trackMax}
          step={1}
          value={Math.min(safe, trackMax)}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={`${label} slider`}
        />
        <div className="near-loc-distance-scale">
          <span>1 km</span>
          <span>{trackMax >= 1000 ? `${(trackMax / 1000).toFixed(1)}k km` : `${trackMax} km`}</span>
        </div>
      </div>
      {hint && <p className="near-loc-hint">{hint}</p>}
    </div>
  );
};

const NearLocationConfigManagement = () => {
  const [form, setForm] = useState(NEAR_LOCATION_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/near-location-config`, { headers: authHeaders() });
      setForm({ ...NEAR_LOCATION_DEFAULTS, ...(res.data || {}) });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cap = positiveKm(form.nearby_distance_km, NEAR_LOCATION_DEFAULTS.nearby_distance_km);
      const defaultRadius = Math.min(
        positiveKm(form.default_search_radius_km, NEAR_LOCATION_DEFAULTS.default_search_radius_km),
        cap,
      );
      const payload = {
        ...form,
        nearby_distance_km: cap,
        default_search_radius_km: defaultRadius,
        fallback_expand_radius_km: positiveKm(
          form.fallback_expand_radius_km,
          NEAR_LOCATION_DEFAULTS.fallback_expand_radius_km,
        ),
        location_refresh_interval_sec: clampInt(form.location_refresh_interval_sec, 15, 3600, 60),
        config_refresh_interval_sec: clampInt(form.config_refresh_interval_sec, 15, 600, 45),
        results_per_page: clampInt(form.results_per_page, 4, 50, 12),
        home_results_limit: clampInt(form.home_results_limit, 4, 24, 8),
      };
      const res = await axios.put(`${API_URL}/admin/near-location-config`, payload, { headers: authHeaders() });
      setForm({ ...NEAR_LOCATION_DEFAULTS, ...(res.data || {}) });
      toast.success('Near Location config saved — changes sync to the app automatically');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-page"><div className="admin-loading">Loading…</div></div>;
  }

  const maxCap = positiveKm(form.nearby_distance_km, 100);

  return (
    <div className="admin-page near-loc-config-page">
      <div className="admin-page-header">
        <div>
          <h1><FiMapPin style={{ verticalAlign: 'middle', marginRight: 8 }} />Near Location Config</h1>
          <p>Control nearby property search for Web and PIF — no code deploy required.</p>
        </div>
      </div>

      <form className="admin-card near-loc-form" onSubmit={handleSave}>
        <div className="near-loc-topbar">
          <div className="near-loc-status-card">
            <span className={`near-loc-status-dot ${form.enabled ? 'is-on' : 'is-off'}`} />
            <div>
              <strong>{form.enabled ? 'Feature is live' : 'Feature is disabled'}</strong>
              <p>
                {form.enabled
                  ? 'Nearby search is available in Web and PIF based on the controls below.'
                  : 'Enable the feature to show nearby location entry points in the app.'}
              </p>
            </div>
          </div>
          <div className="near-loc-topbar-actions">
            <button type="button" className="admin-btn admin-btn--ghost" onClick={load}>
              <FiRefreshCw /> Reload
            </button>
            <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>
              <FiSave /> {saving ? 'Saving…' : 'Save Configuration'}
            </button>
          </div>
        </div>

        <section className="near-loc-section near-loc-section--feature">
          <h2>Feature</h2>
          <div className="near-loc-toggle-grid">
            <label className="near-loc-toggle-card">
              <div className="near-loc-toggle-head">
                <input
                  type="checkbox"
                  checked={Boolean(form.enabled)}
                  onChange={(e) => setField('enabled', e.target.checked)}
                />
                <span>Enable Nearby Location</span>
              </div>
              <p>Master switch for Nearby Locations in Web and PIF.</p>
            </label>

            <label className="near-loc-toggle-card">
              <div className="near-loc-toggle-head">
                <input
                  type="checkbox"
                  checked={Boolean(form.show_on_home)}
                  onChange={(e) => setField('show_on_home', e.target.checked)}
                />
                <span>Show on frontend</span>
              </div>
              <p>Controls the header icon, Nearby page, home section, and nearby actions.</p>
            </label>

            <label className="near-loc-toggle-card">
              <div className="near-loc-toggle-head">
                <input
                  type="checkbox"
                  checked={Boolean(form.allow_manual_location)}
                  onChange={(e) => setField('allow_manual_location', e.target.checked)}
                />
                <span>Allow manual location</span>
              </div>
              <p>Lets users pick a point manually on the map instead of GPS only.</p>
            </label>
          </div>
        </section>

        <section className="near-loc-section">
          <h2>Distance &amp; Results</h2>
          <p className="near-loc-section-intro">
            Set any distance in kilometres — there is no upper limit. Type a value directly or use the slider; the slider range expands as you enter larger values.
          </p>

          <DistanceKmField
            label="Maximum nearby distance (km)"
            hint="Upper cap for nearby searches. Used across Web, PIF, and the Nearby Locations page."
            value={form.nearby_distance_km}
            onChange={(v) => setField('nearby_distance_km', v)}
            sliderMax={Math.max(100, positiveKm(form.nearby_distance_km, 100))}
          />

          <DistanceKmField
            label="Default search radius (km)"
            hint="Initial radius when loading nearby properties. Cannot exceed the maximum distance above."
            value={Math.min(
              positiveKm(form.default_search_radius_km, NEAR_LOCATION_DEFAULTS.default_search_radius_km),
              maxCap,
            )}
            onChange={(v) => setField('default_search_radius_km', Math.min(v, maxCap))}
            sliderMax={maxCap}
          />

          <div className="near-loc-grid">
            <div className="near-loc-field">
              <label>Results per page (Nearby Locations)</label>
              <input
                type="number"
                min={4}
                max={50}
                value={form.results_per_page}
                onChange={(e) => setField('results_per_page', Number(e.target.value))}
              />
            </div>
            <div className="near-loc-field">
              <label>Home section limit</label>
              <input
                type="number"
                min={4}
                max={24}
                value={form.home_results_limit}
                onChange={(e) => setField('home_results_limit', Number(e.target.value))}
              />
            </div>
          </div>
        </section>

        <section className="near-loc-section">
          <h2>Refresh Intervals</h2>
          <div className="near-loc-grid">
            <div className="near-loc-field">
              <label>Location refresh interval (seconds)</label>
              <input
                type="number"
                min={15}
                max={3600}
                value={form.location_refresh_interval_sec}
                onChange={(e) => setField('location_refresh_interval_sec', Number(e.target.value))}
              />
              <p className="near-loc-hint">How often GPS location may be re-fetched on the Nearby page.</p>
            </div>
            <div className="near-loc-field">
              <label>Config sync interval (seconds)</label>
              <input
                type="number"
                min={15}
                max={600}
                value={form.config_refresh_interval_sec}
                onChange={(e) => setField('config_refresh_interval_sec', Number(e.target.value))}
              />
              <p className="near-loc-hint">How often the app pulls updated admin settings.</p>
            </div>
          </div>
        </section>

        <section className="near-loc-section">
          <h2>Fallback when no nearby properties</h2>
          <div className="near-loc-field">
            <label>Fallback behavior</label>
            <select
              value={form.fallback_behavior}
              onChange={(e) => setField('fallback_behavior', e.target.value)}
            >
              <option value="district">Show &quot;View Properties in city&quot; button</option>
              <option value="expand_radius">Auto-expand search radius</option>
              <option value="message">Show message only</option>
              <option value="hide">Hide results section</option>
            </select>
          </div>
          {form.fallback_behavior === 'expand_radius' && (
            <DistanceKmField
              label="Expanded radius (km)"
              hint="Radius used when the initial search returns no results (capped by maximum nearby distance)."
              value={Math.min(
                positiveKm(form.fallback_expand_radius_km, NEAR_LOCATION_DEFAULTS.fallback_expand_radius_km),
                maxCap,
              )}
              onChange={(v) => setField('fallback_expand_radius_km', Math.min(v, maxCap))}
              sliderMax={maxCap}
            />
          )}
          <div className="near-loc-field">
            <label>Empty state message</label>
            <textarea
              rows={2}
              value={form.empty_message}
              onChange={(e) => setField('empty_message', e.target.value)}
            />
          </div>
        </section>

        <section className="near-loc-section">
          <h2>Page Copy</h2>
          <div className="near-loc-field">
            <label>Page title</label>
            <input
              type="text"
              value={form.page_title}
              onChange={(e) => setField('page_title', e.target.value)}
            />
          </div>
          <div className="near-loc-field">
            <label>Page subtitle</label>
            <textarea
              rows={2}
              value={form.page_subtitle}
              onChange={(e) => setField('page_subtitle', e.target.value)}
            />
          </div>
        </section>

        <div className="near-loc-actions">
          <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>
            <FiSave /> {saving ? 'Saving…' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NearLocationConfigManagement;
